import {
  MonthSelector,
  SessionStorageUtils,
  useIsMobile,
} from '@brunommsilva/react-foundations';
import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BalanceHistoryChart from '../components/dashboard/balance-history-chart';
import CashflowsChart from '../components/dashboard/cashflows-chart';
import DashboardBreakdowns from '../components/dashboard/dashboard-breakdowns';
import DashboardSummary from '../components/dashboard/dashboard-summary';
import { TRANSACTIONS_PATH } from '../config/paths';
import useBalanceHistory from '../hooks/balance-history/use-balance-history';
import useCashflows from '../hooks/cashflows/use-cashflows';
import useLayout from '../hooks/layout/use-layout';
import { TransactionsFilter } from '../hooks/transactions/filter/filter';
import TransactionsFilterStorage from '../hooks/transactions/filter/storage';
import { CashflowCategory } from '../models/cashflows/cashflow';
import TransactionType from '../models/transactions/transaction-type-enum';

const DASHBOARD_MONTH_KEY = 'dashboard_selected_month';

const DashboardPage = () => {
  const isMobile = useIsMobile();
  const { topBarHeight, stickyBelowBarZIndex } = useLayout();
  const navigate = useNavigate();

  const [selectedMonth, setSelectedMonth] = useState<string | null>(
    () => SessionStorageUtils.getItem<string>(DASHBOARD_MONTH_KEY) || null
  );
  const { balances, isLoading: isBalancesLoading } = useBalanceHistory();

  const monthsDesc = [...balances].reverse().map(({ month }) => month);

  const changeMonth = (month: string | null) => {
    setSelectedMonth(month);
    if (month) {
      SessionStorageUtils.setItem(DASHBOARD_MONTH_KEY, month);
    } else {
      SessionStorageUtils.removeItem(DASHBOARD_MONTH_KEY);
    }
  };

  useEffect(() => {
    if (!selectedMonth && !!monthsDesc.length) {
      changeMonth(monthsDesc[0]);
    }
  }, [monthsDesc, selectedMonth]);

  const handleCategoryClick = (
    category: CashflowCategory,
    chartType: 'expenses' | 'revenue'
  ) => {
    if (!selectedMonth) return;

    const [yearStr, monthStr] = selectedMonth.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const lastDay = new Date(year, month, 0).getDate();

    const dateFrom = `${selectedMonth}-01`;
    const dateTo = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
    const type =
      chartType === 'expenses' ? TransactionType.DEBIT : TransactionType.CREDIT;

    const drilldownFilter = new TransactionsFilter({
      dateFrom,
      dateTo,
      type,
      category: {
        id: category.id,
        name: category.name,
        icon: category.icon,
      },
    });

    TransactionsFilterStorage.set(drilldownFilter);
    navigate(TRANSACTIONS_PATH);
  };

  const getStartMonth = (endMonth: string | null) => {
    if (!endMonth) {
      return null;
    }
    const [year, month] = endMonth.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    date.setMonth(date.getMonth() - 11);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const startMonth = getStartMonth(selectedMonth);

  const { cashflows, isLoading: isCashflowsLoading } = useCashflows(1, 12, {
    startMonth,
    endMonth: selectedMonth,
  });

  const isDashboardLoading = isCashflowsLoading || isBalancesLoading;

  const currentCashflow = cashflows.find(
    ({ month }) => month === selectedMonth
  );

  const balancesUpToSelected = balances.filter(
    (b) => !selectedMonth || b.month.localeCompare(selectedMonth) <= 0
  );

  const windowedBalances = balancesUpToSelected.slice(-12);
  const months = windowedBalances.map(({ month }) => month);
  const amounts = windowedBalances.map(({ amount }) => amount);

  const revenueValues = cashflows.map(({ revenue }) => revenue);
  const expenseValues = cashflows.map(({ expenses }) => expenses);
  const netResultValues = cashflows.map(({ income }) => income);

  const historicalTotalBalance = balancesUpToSelected.length
    ? balancesUpToSelected[balancesUpToSelected.length - 1].amount
    : 0;

  const income = currentCashflow?.revenue || 0;
  const expenses = currentCashflow?.expenses || 0;
  const netResult = income - expenses;

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          // Stays below the top bar, so that the month is always in sight.
          position: 'sticky',
          top: topBarHeight,
          zIndex: stickyBelowBarZIndex,
          mt: -2,
          mb: 2,
          py: 1.5,
          bgcolor: 'background.default',
          ...(isMobile
            ? {
                // The width of the screen: the padding of the page and of the container, which is 16 px, or 24 from 600 px.
                mx: { xs: -4, sm: -5 },
                px: { xs: 4, sm: 5 },
                borderBottom: 1,
                borderColor: 'divider',
              }
            : { mx: -2, px: 2 }),
        }}
      >
        <MonthSelector
          months={monthsDesc}
          selectedMonth={selectedMonth}
          onMonthChange={changeMonth}
          disabled={isBalancesLoading}
        />
      </Box>

      <DashboardSummary
        income={income}
        expenses={expenses}
        netResult={netResult}
        historicalTotalBalance={historicalTotalBalance}
        isLoading={isDashboardLoading}
        months={months}
        amounts={amounts}
        revenueValues={revenueValues}
        expenseValues={expenseValues}
        netResultValues={netResultValues}
      />

      <DashboardBreakdowns
        categories={currentCashflow?.categories}
        isLoading={isDashboardLoading}
        onCategoryClick={handleCategoryClick}
      />

      <Grid
        container
        spacing={3}
        sx={{
          alignItems: 'stretch',
          mb: 3,
        }}
      >
        <Grid size={12}>
          <Box sx={{ height: '100%' }}>
            <CashflowsChart
              cashflows={cashflows}
              isLoading={isDashboardLoading}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={3}
        sx={{
          alignItems: 'stretch',
          mb: 3,
        }}
      >
        <Grid size={12}>
          <BalanceHistoryChart
            balances={balances}
            isLoading={isDashboardLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
